"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MathOptions2 = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const slate_react_1 = require("slate-react");
const slate_1 = require("slate");
const ArgsPane_1 = require("@jsonjoy.com/ui/lib/4-card/ContextMenu/ArgsPane");
const context_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/context");
const mathlive_1 = require("mathlive");
const use_t_1 = require("use-t");
const context_2 = require("../../../context");
const settings_1 = require("./settings");
const aspect_ratio_1 = tslib_1.__importDefault(require("iconista/lib/react/bootstrap/aspect-ratio"));
const chat_square_text_1 = tslib_1.__importDefault(require("iconista/lib/react/bootstrap/chat-square-text"));
const pen_1 = tslib_1.__importDefault(require("iconista/lib/react/bootstrap/pen"));
const tag_1 = tslib_1.__importDefault(require("iconista/lib/react/bootstrap/tag"));
const SizeIcon = (props) => (React.createElement(aspect_ratio_1.default, { width: 16, height: 16, ...props }));
const CaptionIcon = (props) => (React.createElement(chat_square_text_1.default, { width: 16, height: 16, ...props }));
const NameIcon = (props) => (React.createElement(pen_1.default, { width: 16, height: 16, ...props }));
const LabelIcon = (props) => (React.createElement(tag_1.default, { width: 16, height: 16, ...props }));
const renderSizeIcon = () => React.createElement(SizeIcon, null);
const renderCaptionIcon = () => React.createElement(CaptionIcon, null);
const renderNameIcon = () => React.createElement(NameIcon, null);
const renderLabelIcon = () => React.createElement(LabelIcon, null);
const safeConvert = (fn, tex) => {
    try {
        return fn(tex);
    }
    catch {
        return '';
    }
};
const MathOptions2 = ({ element, closePopup }) => {
    const [t] = (0, use_t_1.useT)();
    const editor = (0, slate_react_1.useSlateStatic)();
    const mutxt = (0, context_2.useMuTxt)();
    const popup = (0, context_1.usePopup)();
    mutxt.things.version.use();
    const onCancel = React.useCallback(() => {
        if (closePopup)
            closePopup();
        else
            popup?.close();
    }, [closePopup, popup]);
    const elementRef = React.useRef(element);
    elementRef.current = element;
    const thingId = element['@thing'] ?? '';
    const thing = thingId ? mutxt.things.get(thingId) : undefined;
    const tex = thing?.val ?? '';
    const setElementField = React.useCallback((field, value) => {
        const e = elementRef.current;
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, e);
            if (value === undefined || value === '') {
                slate_1.Transforms.unsetNodes(editor, field, { at: path });
            }
            else {
                slate_1.Transforms.setNodes(editor, { [field]: value }, { at: path });
            }
        }
        catch { }
    }, [editor]);
    const setThingField = React.useCallback((field, value) => {
        if (!thingId)
            return;
        const current = mutxt.things.get(thingId);
        if (!current)
            return;
        const prev = current[field] ?? '';
        const next = value ?? '';
        if (prev === next)
            return;
        mutxt.things.update(thingId, { [field]: next || undefined });
        mutxt.sync(false);
    }, [mutxt, thingId]);
    const ascii = React.useMemo(() => (tex ? safeConvert(mathlive_1.convertLatexToAsciiMath, tex) : ''), [tex]);
    const mathMl = React.useMemo(() => (tex ? safeConvert(mathlive_1.convertLatexToMathMl, tex) : ''), [tex]);
    const mathHtml = React.useMemo(() => (tex ? safeConvert(mathlive_1.convertLatexToMarkup, tex) : ''), [tex]);
    const item = React.useMemo(() => ({ name: t('Equation options'), compact: true }), [t]);
    const params = React.useMemo(() => {
        const list = [];
        list.push({
            kind: 'select',
            id: 'size',
            name: t('Size'),
            icon: renderSizeIcon,
            default: (0, settings_1.getMathSize)(element.size),
            options: settings_1.MATH_SIZES.map((s) => ({
                id: s,
                name: `${s} — ${settings_1.MATH_SIZE_LABEL[s]}`,
            })),
        });
        list.push({
            kind: 'str',
            id: 'caption',
            name: t('Caption'),
            icon: renderCaptionIcon,
            optional: true,
            placeholder: t('Shown below the equation'),
            default: element.caption ?? '',
        });
        list.push({ name: t('Reference'), heading: true, collapsible: true, initialCollapsed: true });
        list.push({
            kind: 'str',
            id: 'name',
            name: t('Name'),
            icon: renderNameIcon,
            optional: true,
            placeholder: t('e.g. Pythagorean theorem'),
            default: thing?.name ?? '',
        });
        list.push({
            kind: 'str',
            id: 'label',
            name: t('Label'),
            icon: renderLabelIcon,
            optional: true,
            placeholder: t('e.g. eq:pythagoras'),
            default: thing?.label ?? '',
        });
        if (tex) {
            list.push({ name: t('Source'), heading: true, collapsible: true, initialCollapsed: false });
            list.push({
                kind: 'code',
                id: 'tex',
                name: 'LaTeX',
                variant: 'block',
                value: tex,
            });
            if (ascii) {
                list.push({
                    kind: 'code',
                    id: 'ascii',
                    name: 'ASCII Math',
                    variant: 'block',
                    value: ascii,
                });
            }
            if (mathMl) {
                list.push({
                    kind: 'code',
                    id: 'mathMl',
                    name: 'MathML',
                    variant: 'block',
                    value: mathMl,
                });
            }
            if (mathHtml) {
                list.push({
                    kind: 'code',
                    id: 'mathHtml',
                    name: 'HTML',
                    variant: 'block',
                    value: mathHtml,
                });
            }
        }
        return list;
    }, [t, element.size, element.caption, thing?.name, thing?.label, tex, ascii, mathMl, mathHtml]);
    const onChange = React.useCallback((_list, map) => {
        const size = (0, settings_1.getMathSize)(map.size);
        setElementField('size', (0, settings_1.getStoredMathSize)(size));
        const caption = map.caption ?? '';
        setElementField('caption', caption.trim() ? caption : undefined);
        const name = map.name ?? '';
        setThingField('name', name.trim() ? name : undefined);
        const label = map.label ?? '';
        setThingField('label', label.trim() ? label : undefined);
    }, [setElementField, setThingField]);
    return React.createElement(ArgsPane_1.ArgsPane, { item: item, params: params, onCancel: onCancel, onChange: onChange, minWidth: 360 });
};
exports.MathOptions2 = MathOptions2;
